import React from "react";
import { Helmet } from "react-helmet";
import Header from "../Header";
import Footer from "../Footer";
import "./AndroidAppDevelopment.css";

export default function AndroidAppDevelopment() {
    const services = [
        {
            title: "Custom Android App Development",
            description: "Build native Android apps for startups, businesses, game studios, and product teams with clean architecture, modern UI, scalable APIs, and release-ready builds.",
            points: ["Native Android apps", "Product architecture", "Play Store builds"]
        },
        {
            title: "Business & Enterprise Apps",
            description: "Create secure apps for operations, field teams, CRM, booking, reporting, dashboards, workflow automation, and internal productivity.",
            points: ["Role-based access", "Dashboards", "Secure workflows"]
        },
        {
            title: "On-Demand Android Apps",
            description: "Develop booking, delivery, marketplace, service, ecommerce, education, healthcare, gaming, and location-based Android applications.",
            points: ["Maps & tracking", "Payments", "Notifications"]
        },
        {
            title: "Android App Modernization",
            description: "Improve existing Android apps with UI upgrades, performance optimization, API updates, bug fixing, analytics, and Play Store compliance.",
            points: ["UI refresh", "Performance fixes", "API upgrades"]
        }
    ];

    const heroStats = [
        { value: "Native", label: "Android Apps" },
        { value: "API", label: "Backend Ready" },
        { value: "QA", label: "Device Tested" }
    ];

    const capabilities = [
        "Kotlin",
        "Java",
        "Android Studio",
        "Material UI",
        "REST APIs",
        "Firebase",
        "Push Notifications",
        "Payment Gateway",
        "Maps & GPS",
        "Authentication",
        "Analytics",
        "Offline Support",
        "In-App Purchases",
        "Game Services",
        "Play Store Publishing",
        "QA Testing",
        "Maintenance"
    ];

    const appTypes = [
        "Ecommerce Apps",
        "Booking Apps",
        "Delivery Apps",
        "Healthcare Apps",
        "Education Apps",
        "Fintech Apps",
        "Enterprise Apps",
        "Game Apps",
        "Social Apps",
        "Media Apps",
        "Utility Apps"
    ];

    const productModules = [
        { title: "User Accounts", description: "Secure sign-up, login, profile management, roles, permissions, and session handling." },
        { title: "Admin Dashboard", description: "Manage users, content, orders, bookings, reports, settings, and app activity from one place." },
        { title: "Payments & Wallets", description: "Integrate payment gateways, subscriptions, wallet flows, invoices, refunds, and transaction history." },
        { title: "Maps & Location", description: "Add GPS, live tracking, geofencing, nearby search, delivery routes, and location-based features." },
        { title: "Notifications", description: "Use push notifications, transactional alerts, reminders, campaigns, and event-based messaging." },
        { title: "Game App Features", description: "Build leaderboards, achievements, in-app purchases, rewards, player profiles, matchmaking basics, and event tracking." },
        { title: "Analytics & Reports", description: "Track user behavior, app events, revenue, retention, conversion, and operational performance." }
    ];

    const engagementModels = [
        {
            title: "MVP Android App",
            description: "Best for validating a new idea with essential features, clean UX, backend basics, QA, and a launch-ready first version.",
            items: ["Core user journey", "Essential APIs", "Play Store support"]
        },
        {
            title: "Full Product Build",
            description: "Best for businesses that need a complete Android product with admin panel, integrations, analytics, and scalable architecture.",
            items: ["Full feature set", "Backend & admin", "QA and release"]
        },
        {
            title: "App Upgrade & Support",
            description: "Best for existing apps that need bug fixing, UI improvement, performance work, API updates, maintenance, and new features.",
            items: ["Audit and fixes", "Feature upgrades", "Ongoing support"]
        }
    ];

    const process = [
        { step: "01", title: "Discovery", description: "We define app goals, users, features, integrations, backend needs, monetization, timeline, and launch plan." },
        { step: "02", title: "UX & Architecture", description: "We plan app flows, wireframes, UI direction, API contracts, data structure, permissions, and technical architecture." },
        { step: "03", title: "Development", description: "Our team builds the Android app, backend integrations, admin tools, notifications, payments, analytics, and feature modules." },
        { step: "04", title: "QA & Optimization", description: "We test devices, OS versions, performance, security, usability, crashes, edge cases, and Play Store readiness." },
        { step: "05", title: "Launch & Support", description: "We support Play Store publishing, monitoring, updates, bug fixes, new features, and long-term maintenance." }
    ];

    const benefits = [
        { icon: "fa-brands fa-android", title: "Android-Focused Delivery", description: "We build Android apps around platform behavior, device coverage, Play Store rules, and user expectations." },
        { icon: "fa-solid fa-mobile-screen-button", title: "Clean Mobile UX", description: "Interfaces are designed for fast actions, readable screens, clear navigation, and responsive mobile behavior." },
        { icon: "fa-solid fa-server", title: "Backend Integration", description: "We connect APIs, databases, admin panels, authentication, payments, notifications, analytics, and cloud services." },
        { icon: "fa-solid fa-shield-check", title: "QA & Store Readiness", description: "We test app stability, performance, permissions, device compatibility, crashes, and release compliance." }
    ];

    const faqs = [
        {
            question: "What Android app development services does Oengines provide?",
            answer: "Oengines provides custom Android app development, UI/UX, backend integration, Firebase, APIs, payments, maps, notifications, QA, Play Store publishing, and maintenance."
        },
        {
            question: "Do you build native Android apps?",
            answer: "Yes. We build native Android apps using Kotlin, Java, Android Studio, Material UI patterns, and modern mobile development practices."
        },
        {
            question: "Can you build backend and admin panel with the Android app?",
            answer: "Yes. We can build APIs, databases, admin dashboards, authentication, reporting, analytics, and cloud deployment along with the Android app."
        },
        {
            question: "Can you publish the app on Google Play Store?",
            answer: "Yes. We help prepare release builds, store assets, app details, compliance checks, testing, and Play Store publishing support."
        },
        {
            question: "Do you build Android game apps?",
            answer: "Yes. We build Android game apps with user profiles, rewards, leaderboards, in-app purchases, analytics, backend APIs, QA, and Play Store release support."
        }
    ];

    const structuredData = {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": "Android App Development Services",
        "provider": {
            "@type": "Organization",
            "name": "Oengines Games LLP",
            "url": "https://www.oengines.com"
        },
        "serviceType": "Android App Development",
        "areaServed": "Worldwide",
        "description": "Android app development services for native apps, business apps, game apps, on-demand apps, backend integrations, QA, Play Store publishing, and maintenance.",
        "offers": services.map((service) => ({
            "@type": "Offer",
            "itemOffered": {
                "@type": "Service",
                "name": service.title,
                "description": service.description
            }
        }))
    };

    return (
        <>
            <Helmet>
                <title>Android App Development Company | Native Android & Game Apps | Oengines</title>
                <meta name="description" content="Oengines Games LLP builds native Android apps and game apps with Kotlin, Java, UI/UX, backend APIs, Firebase, payments, maps, notifications, QA, Play Store publishing, and maintenance support." />
                <meta name="keywords" content="Android app development company, Android app development services, native Android app development, Android game app development, Kotlin app development, Java Android development, mobile app development company" />
                <link rel="canonical" href="https://www.oengines.com/android-app-development" />
                <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />
            </Helmet>
            <Header />
            <main className="android-app-page">
                <section className="android-app-hero">
                    <div className="container">
                        <div className="row align-items-center g-4">
                            <div className="col-lg-7">
                                <span className="android-app-eyebrow">Android App Development</span>
                                <h1>Build Native Android Apps for Business, Games & Growth</h1>
                                <p>Oengines Games LLP develops Android applications and game apps with clean UI/UX, reliable backend integrations, secure architecture, strong QA, Play Store readiness, and long-term product support.</p>
                                <div className="android-app-actions">
                                    <a href="/contact" className="as-btn">Start a Project <i className="fa-regular fa-arrow-right ms-2"></i></a>
                                    <a href="tel:+912614054978" className="android-app-call">Call: (0261) - 4054978</a>
                                </div>
                                <div className="android-app-hero-stats">
                                    {heroStats.map((item) => (
                                        <div key={item.label}>
                                            <strong>{item.value}</strong>
                                            <span>{item.label}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="col-lg-5">
                                <div className="android-app-hero-card">
                                    <img src="assets/img/bg/hero-bg/app.jpg" alt="Android app development" />
                                    <div>
                                        <strong>Native Android Delivery</strong>
                                        <span>Kotlin, Java, UI/UX, APIs, Firebase, game features, QA, Play Store, and maintenance.</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="android-app-section space">
                    <div className="container">
                        <div className="android-app-heading text-center">
                            <span>Android Services</span>
                            <h2>Android App Development Services for Modern Digital Products</h2>
                            <p>We help you build Android apps that are usable, scalable, stable, and ready for real users across different devices and Android versions.</p>
                        </div>
                        <div className="row g-4">
                            {services.map((service) => (
                                <div className="col-lg-3 col-md-6" key={service.title}>
                                    <article className="android-app-card">
                                        <h3>{service.title}</h3>
                                        <p>{service.description}</p>
                                        <ul>
                                            {service.points.map((point) => <li key={point}>{point}</li>)}
                                        </ul>
                                    </article>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="android-app-model-section space">
                    <div className="container">
                        <div className="android-app-heading text-center">
                            <span>Development Options</span>
                            <h2>Choose the Right Android App Development Path</h2>
                            <p>Whether you need a fast MVP, a complete product, or improvements to an existing app, we shape the delivery plan around your business stage.</p>
                        </div>
                        <div className="android-app-model-grid">
                            {engagementModels.map((model) => (
                                <article className="android-app-model-card" key={model.title}>
                                    <h3>{model.title}</h3>
                                    <p>{model.description}</p>
                                    <ul>
                                        {model.items.map((item) => <li key={item}>{item}</li>)}
                                    </ul>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="android-app-tech-section space">
                    <div className="container">
                        <div className="android-app-tech-panel">
                            <div className="android-app-heading mb-0">
                                <span>Technology & Integrations</span>
                                <h2>Everything Needed for Production-Ready Android Apps</h2>
                                <p>From UI and native development to APIs, cloud, analytics, payments, and publishing, we cover the full Android product lifecycle.</p>
                            </div>
                            <div className="android-app-tech-list">
                                {capabilities.map((item) => <span key={item}>{item}</span>)}
                            </div>
                        </div>
                    </div>
                </section>

                <section className="android-app-types-section space">
                    <div className="container">
                        <div className="android-app-heading text-center">
                            <span>App Types</span>
                            <h2>Android Apps We Can Build for Your Business</h2>
                        </div>
                        <div className="android-app-types-list">
                            {appTypes.map((item) => <span key={item}>{item}</span>)}
                        </div>
                    </div>
                </section>

                <section className="android-app-module-section space">
                    <div className="container">
                        <div className="android-app-heading text-center">
                            <span>Feature Modules</span>
                            <h2>Android App Features We Commonly Build</h2>
                            <p>These modules can be combined based on your product type, user roles, business workflow, and launch priorities.</p>
                        </div>
                        <div className="android-app-module-grid">
                            {productModules.map((module) => (
                                <article className="android-app-module-card" key={module.title}>
                                    <h3>{module.title}</h3>
                                    <p>{module.description}</p>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="android-app-process-section space">
                    <div className="container">
                        <div className="android-app-heading text-center">
                            <span>Development Process</span>
                            <h2>From Android App Idea to Play Store Launch</h2>
                        </div>
                        <div className="android-app-process-grid">
                            {process.map((item) => (
                                <article className="android-app-process-card" key={item.step}>
                                    <span>{item.step}</span>
                                    <h3>{item.title}</h3>
                                    <p>{item.description}</p>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="android-app-benefit-section space">
                    <div className="container">
                        <div className="android-app-heading text-center">
                            <span>Why Choose Oengines</span>
                            <h2>Android App Development Backed by Design, Backend & QA</h2>
                        </div>
                        <div className="row g-4">
                            {benefits.map((item) => (
                                <div className="col-lg-3 col-md-6" key={item.title}>
                                    <article className="android-app-benefit-card">
                                        <span><i className={item.icon}></i></span>
                                        <h3>{item.title}</h3>
                                        <p>{item.description}</p>
                                    </article>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="android-app-faq-section space">
                    <div className="container">
                        <div className="android-app-heading text-center">
                            <span>FAQ</span>
                            <h2>Android App Development Questions</h2>
                        </div>
                        <div className="android-app-faq-grid">
                            {faqs.map((item) => (
                                <article className="android-app-faq-card" key={item.question}>
                                    <h3>{item.question}</h3>
                                    <p>{item.answer}</p>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="android-app-cta">
                    <div className="container">
                        <div className="android-app-cta-box">
                            <div>
                                <span>Build for Android</span>
                                <h2>Plan your Android app with Oengines.</h2>
                                <p>Share your app idea, target users, required features, backend needs, and launch goals. We will help define the right development path.</p>
                            </div>
                            <a href="/contact" className="as-btn">Book Consultation <i className="fa-regular fa-arrow-right ms-2"></i></a>
                        </div>
                    </div>
                </section>
            </main>
            <Footer />
        </>
    );
}
